import { useStore } from "@nanostores/react"
import { useEffect } from "react"

import { ColorScheme } from "shared/context/color-scheme/ColorScheme"
import { getDocument } from "shared/lib/getDocument"
import { getWindow } from "shared/lib/getWindow"

import { atom } from "nanostores"

const colorSchemeAtom = atom<ColorScheme>(ColorScheme.SYSTEM)

// Get the actual scheme to apply (resolves SYSTEM to LIGHT/DARK)
function getResolvedScheme(
  scheme: ColorScheme
): ColorScheme.LIGHT | ColorScheme.DARK {
  if (scheme === ColorScheme.SYSTEM) {
    return getWindow()?.matchMedia("(prefers-color-scheme: dark)").matches
      ? ColorScheme.DARK
      : ColorScheme.LIGHT
  }
  return scheme as ColorScheme.LIGHT | ColorScheme.DARK
}

// Apply the color scheme to the DOM
function applyColorScheme(scheme: ColorScheme) {
  const resolved = getResolvedScheme(scheme)
  getDocument()?.documentElement.style.setProperty("color-scheme", resolved)
  getDocument()?.documentElement.setAttribute("data-color-scheme", resolved)
}

// Set the color scheme preference
function setColorScheme(scheme: ColorScheme) {
  colorSchemeAtom.set(scheme)

  if (scheme === ColorScheme.SYSTEM) {
    getWindow()?.localStorage.removeItem("prefers-color-scheme")
  } else {
    getWindow()?.localStorage.setItem("prefers-color-scheme", scheme)
  }

  applyColorScheme(scheme)
}

export function useColorSchemeController() {
  const colorScheme = useStore(colorSchemeAtom)

  useEffect(() => {
    // Initialize from localStorage on mount
    const stored = getWindow()?.localStorage.getItem(
      "prefers-color-scheme"
    ) as ColorScheme | null
    if (stored && stored !== ColorScheme.SYSTEM) {
      colorSchemeAtom.set(stored)
    }

    // Apply current scheme
    applyColorScheme(colorScheme)

    // Listen for system preference changes
    const colorSchemeMediaQuery = getWindow()?.matchMedia(
      "(prefers-color-scheme: dark)"
    )
    const handleChange = () => {
      if (colorScheme === ColorScheme.SYSTEM) {
        applyColorScheme(ColorScheme.SYSTEM)
      }
    }

    colorSchemeMediaQuery?.addEventListener("change", handleChange)
    return () =>
      colorSchemeMediaQuery?.removeEventListener("change", handleChange)
  }, [colorScheme])

  return [colorScheme, setColorScheme] as const
}

export function useColorScheme() {
  const preference = useStore(colorSchemeAtom)
  const activeColorScheme = getResolvedScheme(preference)

  return {
    activeColorScheme,
    preferredColorScheme: preference,
    setPreference: setColorScheme
  }
}
